/** The framework band a level falls in; the last band catches everything above. */
export function frameworkBandForLevel(config, levelNumber) {
    const bands = config.curve.bands;
    for (const band of bands) {
        if (band.levelRange.max === null || levelNumber <= band.levelRange.max)
            return band;
    }
    return bands[bands.length - 1];
}
/**
 * Where a level sits inside a level range, 0 (first level) → 1 (last), clamped.
 * An open-ended range (`last === null`) has no last level, so it eases towards 1
 * over a range's worth of levels rather than snapping there.
 *
 * Shared by the score curve and WP-35.11's block-count ramp, so the two climb in
 * step instead of each inventing its own shape.
 */
export function rampPosition(first, last, levelNumber) {
    if (last === null) {
        // Open-ended band: approach the top asymptotically, never exceeding it.
        const span = Math.max(1, first);
        return Math.max(0, 1 - 1 / (1 + (levelNumber - first) / span));
    }
    if (last <= first)
        return 1;
    return Math.min(1, Math.max(0, (levelNumber - first) / (last - first)));
}
function positionInBand(band, levelNumber) {
    return rampPosition(band.levelRange.min, band.levelRange.max, levelNumber);
}
/**
 * The level span a GAMEPLAY band (Easy/Medium/Hard/Expert) covers, derived from
 * the framework bands that map onto it — e.g. with the shipped curve, gameplay
 * `Medium` is Easy + Easy+ + Medium = levels 11–70. `max: null` marks the
 * open-ended tail.
 *
 * This is the ONLY source of a gameplay band's level span. `config.bands[]` used
 * to carry its own `levelRange` holding the legacy single-axis ranges (Easy
 * 1–15, …) and disagreeing with this by five levels; WP-50.23 deleted it.
 */
export function gameplayBandSpan(config, difficulty) {
    const bands = config.curve.bands.filter((b) => b.difficulty === difficulty);
    if (bands.length === 0)
        return { min: 1, max: null };
    const min = Math.min(...bands.map((b) => b.levelRange.min));
    const openEnded = bands.some((b) => b.levelRange.max === null);
    const max = openEnded ? null : Math.max(...bands.map((b) => b.levelRange.max));
    return { min, max };
}
/**
 * Ramp a per-FRAMEWORK-band `{min,max}` across the band containing `levelNumber`
 * (WP-36.6) — the single seam every progression ramp goes through, so block
 * count, colour count and arrow density all climb on the same shape.
 *
 * The returned value is deliberately unrounded: callers that want a count round
 * it, and `proposeLocks` multiplies it by a block count first. Rounding here
 * would throw away the fractional part the arrow fraction is entirely made of.
 */
export function frameworkBandRamp(config, levelNumber, ranges) {
    const band = frameworkBandForLevel(config, levelNumber);
    const range = ranges[band.name];
    const position = rampPosition(band.levelRange.min, band.levelRange.max, levelNumber);
    return range.min + (range.max - range.min) * position;
}
/** Is this a recovery level? Every Nth level, but never level 1. */
export function isRecoveryLevel(config, levelNumber) {
    const every = config.recovery.everyNLevels;
    if (every <= 0 || levelNumber <= 1)
        return false;
    return levelNumber % every === 0;
}
/** Is this one of the configured milestone levels? */
export function isMilestoneLevel(config, levelNumber) {
    return config.milestones.levels.includes(levelNumber);
}
/**
 * The target score for a level: ramp across the band's window, then apply the
 * two deliberate deviations.
 *
 * Milestone wins over recovery when a level is both. A milestone is a designed,
 * announced peak (§18) while recovery is unannounced pacing (§17) — landing a
 * milestone on a dip would make the memorable level the easy one.
 */
export function targetScoreForLevel(config, levelNumber) {
    const band = frameworkBandForLevel(config, levelNumber);
    const { min, max } = band.targetScore;
    const ramped = min + (max - min) * positionInBand(band, levelNumber);
    const isMilestone = isMilestoneLevel(config, levelNumber);
    const isRecovery = !isMilestone && isRecoveryLevel(config, levelNumber);
    let target = ramped;
    if (isMilestone) {
        target = min + (max - min) * config.curve.milestoneLift;
    }
    else if (isRecovery) {
        // Drop by `dropBands` band-widths, floored at the very first band's minimum
        // so a dip early on cannot ask for a negative score.
        const width = max - min;
        const floor = config.curve.bands[0].targetScore.min;
        target = Math.max(floor, ramped - width * config.recovery.dropBands);
    }
    return {
        band,
        difficulty: band.difficulty,
        target,
        tolerance: config.curve.tolerance,
        isRecovery,
        isMilestone,
    };
}
/**
 * Which framework band a measured score belongs to — the inverse of the curve,
 * used to report whether a board landed where it was aimed. Windows are
 * contiguous and touch at their endpoints, so a score exactly on a boundary is
 * resolved to the EASIER band (the first match wins); anything above every
 * window is the last band.
 */
export function bandForScore(config, score) {
    const bands = config.curve.bands;
    for (const band of bands) {
        if (score >= band.targetScore.min && score <= band.targetScore.max)
            return band;
    }
    if (score < bands[0].targetScore.min)
        return bands[0];
    return bands[bands.length - 1];
}
